import React, { useState, useMemo } from 'react';
import { Avatar, Button, Spin, Tag, Typography, Input, theme } from 'antd';
import Drawer from '../../../components/AppDrawer';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  CommentOutlined,
  SendOutlined,
  UserOutlined,
  TeamOutlined,
  FlagOutlined,
} from '@ant-design/icons';
import { useTaskItem } from '../hooks/useTaskItem';
import { useAddTaskComment } from '../hooks/useAddTaskComment';
import { useUserOptions } from '../../../hooks/options/useUserOptions';
import { useContactOptions } from '../../../hooks/options/useContactOptions';
import { coerceEnumByLabel } from '../../../lib/api/coerceEnum';
import { contactDisplayName } from '../../contacts/contactType';
import {
  TASK_STATUS_LABELS,
  TASK_STATUS_COLORS,
  TASK_PRIORITY_LABELS,
  TASK_PRIORITY_COLORS,
  TASK_TYPE_LABELS,
  TASK_TYPE_SHORT_LABELS,
  isTerminalStatus,
  normalizeTaskStatus,
  normalizeTaskType,
} from '../constants';
import type { TaskItemStatus, TaskCommentDto, TaskPriority } from '../types';
import dayjs from 'dayjs';
import './TaskDetailDrawer.css';

const { Text } = Typography;
const { TextArea } = Input;

const TASK_PRIORITY_BY_LABEL = Object.fromEntries(
  Object.entries(TASK_PRIORITY_LABELS).map(([value, label]) => [label, Number(value) as TaskPriority]),
) as Record<string, TaskPriority>;

interface TaskDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  taskId: string | null;
}

function formatDate(s: string | null | undefined): string {
  if (s == null || s === '') return '—';
  try {
    return dayjs(s).format('MMM D, YYYY · h:mm A');
  } catch {
    return s;
  }
}

function relativeTime(s: string): string {
  const diffMs = Date.now() - new Date(s).getTime();
  if (Number.isNaN(diffMs)) return '';
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return dayjs(s).format('MMM D, YYYY');
}

function isOverdue(
  dueDate: string | null | undefined,
  status: TaskItemStatus,
): boolean {
  if (isTerminalStatus(status) || !dueDate) return false;
  const d = dayjs(dueDate);
  if (!d.isValid()) return false;
  return d.isBefore(dayjs(), 'day');
}

function isDueToday(dueDate: string | null | undefined): boolean {
  if (!dueDate) return false;
  const d = dayjs(dueDate);
  if (!d.isValid()) return false;
  return d.isSame(dayjs(), 'day');
}

function getInitials(name: string | null | undefined): string {
  if (!name?.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
}

function heroClassName(status: TaskItemStatus, dueDate: string | null): string {
  if (status === 3) return 'cp-task-drawer__hero cp-task-drawer__hero--completed';
  if (status === 4) return 'cp-task-drawer__hero cp-task-drawer__hero--cancelled';
  if (status === 2) return 'cp-task-drawer__hero cp-task-drawer__hero--blocked';
  if (isOverdue(dueDate, status)) return 'cp-task-drawer__hero cp-task-drawer__hero--overdue';
  return 'cp-task-drawer__hero';
}

function DetailTile({
  icon,
  label,
  children,
  tone = 'default',
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
  tone?: 'default' | 'overdue' | 'today';
}) {
  const tileClass =
    tone === 'overdue'
      ? 'cp-task-drawer__tile cp-task-drawer__tile--highlight'
      : tone === 'today'
        ? 'cp-task-drawer__tile cp-task-drawer__tile--today'
        : 'cp-task-drawer__tile';

  return (
    <div className={tileClass}>
      <div className="cp-task-drawer__tile-label">
        {icon}
        <span>{label}</span>
      </div>
      <div className="cp-task-drawer__tile-value">{children}</div>
    </div>
  );
}

const TaskDetailDrawer: React.FC<TaskDetailDrawerProps> = ({ open, onClose, taskId }) => {
  const { token } = theme.useToken();
  const { data: task, isLoading } = useTaskItem(open ? taskId : null);
  const { data: users = [] } = useUserOptions();
  const { data: contacts = [] } = useContactOptions();
  const addComment = useAddTaskComment(taskId);
  const [commentText, setCommentText] = useState('');

  const contactOptions = useMemo(
    () =>
      contacts.map((c) => ({
        value: c.id,
        label: contactDisplayName(c),
      })),
    [contacts]
  );

  const userLookup = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users) {
      m.set(u.id, u.label);
    }
    return m;
  }, [users]);

  const handleAddComment = () => {
    if (!commentText.trim()) return;
    addComment.mutate(commentText.trim());
    setCommentText('');
  };

  const taskStatus = task ? normalizeTaskStatus(task.status) : 0;
  const taskPriority = task
    ? coerceEnumByLabel(task.priority, TASK_PRIORITY_BY_LABEL) ?? 1
    : 1;
  const taskType = task ? normalizeTaskType(task.taskType) : 0;

  const dueTone: 'default' | 'overdue' | 'today' = task
    ? isOverdue(task.dueDate, taskStatus)
      ? 'overdue'
      : isDueToday(task.dueDate)
        ? 'today'
        : 'default'
    : 'default';

  const assigneeName = task?.assigneeId
    ? userLookup.get(task.assigneeId) ?? task.assigneeDisplayName ?? '—'
    : 'Unassigned';

  const contactName = task?.contactId
    ? contactOptions.find((c) => c.value === task.contactId)?.label ?? '—'
    : '—';

  const drawerTitle = task ? (
    <div className="cp-task-drawer__title-wrap">
      <span className="cp-task-drawer__title-text">{task.title}</span>
      <span className="cp-task-drawer__title-sub">
        {TASK_TYPE_SHORT_LABELS[taskType]} · {TASK_PRIORITY_LABELS[taskPriority]} priority
      </span>
    </div>
  ) : (
    'Activity'
  );

  const sortedComments = [...(task?.comments ?? [])].sort(
    (a, b) => new Date(b.createdAtUtc).getTime() - new Date(a.createdAtUtc).getTime()
  );

  return (
    <Drawer
      className="cp-task-drawer"
      title={drawerTitle}
      placement="right"
      width={680}
      open={open}
      onClose={onClose}
      destroyOnClose
      styles={{
        body: { padding: 0, background: token.colorBgLayout },
      }}
    >
      {isLoading ? (
        <div className="cp-task-drawer__loading">
          <Spin size="large" />
        </div>
      ) : !task ? (
        <div className="cp-task-drawer__inner">
          <Text type="secondary">Activity not found.</Text>
        </div>
      ) : (
        <div className="cp-task-drawer__inner">
          <div className={heroClassName(taskStatus, task.dueDate)}>
            <div className="cp-task-drawer__hero-row">
              <Tag color={TASK_STATUS_COLORS[taskStatus]}>{TASK_STATUS_LABELS[taskStatus]}</Tag>
              <Tag color={taskType === 1 ? 'blue' : 'default'}>
                {TASK_TYPE_LABELS[taskType]}
              </Tag>
              <Tag color={TASK_PRIORITY_COLORS[taskPriority]}>
                {TASK_PRIORITY_LABELS[taskPriority]}
              </Tag>
              {isOverdue(task.dueDate, taskStatus) && (
                <Tag color="error">Overdue</Tag>
              )}
              {isDueToday(task.dueDate) && !isOverdue(task.dueDate, taskStatus) && (
                <Tag color="warning">Due today</Tag>
              )}
            </div>
          </div>

          {task.description ? (
            <section className="cp-task-drawer__section">
              <div className="cp-task-drawer__section-head">Description</div>
              <div className="cp-task-drawer__section-body">
                <p className="cp-task-drawer__description">{task.description}</p>
              </div>
            </section>
          ) : null}

          {task.snoozeNote ? (
            <section className="cp-task-drawer__section">
              <div className="cp-task-drawer__section-head">Snooze reason</div>
              <div className="cp-task-drawer__section-body">
                <p className="cp-task-drawer__description">{task.snoozeNote}</p>
              </div>
            </section>
          ) : null}

          <section className="cp-task-drawer__section">
            <div className="cp-task-drawer__section-head">Details</div>
            <div className="cp-task-drawer__section-body">
              <div className="cp-task-drawer__tile-grid">
                <DetailTile icon={<UserOutlined />} label="Assignee">
                  <Text>{assigneeName}</Text>
                </DetailTile>

                {taskType === 1 ? (
                  <>
                    <DetailTile icon={<TeamOutlined />} label="Contact">
                      <Text>{contactName}</Text>
                    </DetailTile>
                    <DetailTile icon={<FlagOutlined />} label="Lead">
                      <Text>{task.leadTitle ?? '—'}</Text>
                    </DetailTile>
                  </>
                ) : (
                  <DetailTile icon={<TeamOutlined />} label="Contact">
                    <Text>{contactName}</Text>
                  </DetailTile>
                )}

                <DetailTile
                  icon={<CalendarOutlined />}
                  label="Due date"
                  tone={dueTone}
                >
                  <Text strong={dueTone !== 'default'}>
                    {formatDate(task.dueDate)}
                  </Text>
                </DetailTile>

                <DetailTile icon={<ClockCircleOutlined />} label="Issue date">
                  <Text>{formatDate(task.createdAtUtc)}</Text>
                </DetailTile>
              </div>
            </div>
          </section>

          <section className="cp-task-drawer__section">
            <div className="cp-task-drawer__section-head">
              Comments
              {sortedComments.length > 0 && (
                <Text type="secondary" style={{ fontWeight: 400, marginLeft: 8, fontSize: 12 }}>
                  ({sortedComments.length})
                </Text>
              )}
            </div>
            <div className="cp-task-drawer__section-body">
              {sortedComments.length === 0 ? (
                <div className="cp-task-drawer__empty">
                  <CommentOutlined />
                  <span className="cp-task-drawer__empty-text">
                    No comments yet — add the first note below
                  </span>
                </div>
              ) : (
                <div className="cp-task-drawer__comments-list">
                  {sortedComments.map((item: TaskCommentDto) => (
                    <article key={item.id} className="cp-task-drawer__comment">
                      <Avatar
                        size={36}
                        style={{
                          backgroundColor: token.colorPrimary,
                          flexShrink: 0,
                          fontSize: 13,
                        }}
                      >
                        {getInitials(item.authorDisplayName)}
                      </Avatar>
                      <div className="cp-task-drawer__comment-bubble">
                        <div className="cp-task-drawer__comment-meta">
                          <span className="cp-task-drawer__comment-author">
                            {item.authorDisplayName ?? 'Unknown'}
                          </span>
                          <span
                            className="cp-task-drawer__comment-time"
                            title={formatDate(item.createdAtUtc)}
                          >
                            {relativeTime(item.createdAtUtc)}
                          </span>
                        </div>
                        <p className="cp-task-drawer__comment-body">{item.content}</p>
                      </div>
                    </article>
                  ))}
                </div>
              )}

              <div className="cp-task-drawer__composer">
                <TextArea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Write a comment…"
                  rows={3}
                  maxLength={2000}
                  className="cp-task-drawer__composer-input"
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      handleAddComment();
                    }
                  }}
                />
                <div className="cp-task-drawer__composer-actions">
                  <span className="cp-task-drawer__composer-hint">
                    Enter to send · Shift+Enter for new line
                  </span>
                  <div className="cp-task-drawer__composer-footer">
                    <span className="cp-task-drawer__composer-count">
                      {commentText.length} / 2000
                    </span>
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      onClick={handleAddComment}
                      loading={addComment.isPending}
                      disabled={!commentText.trim()}
                    >
                      Send
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      )}
    </Drawer>
  );
};

export default TaskDetailDrawer;
